import { useEffect, useState } from 'react'
import Spreadsheet, { CellBase, DataEditor, Matrix, createEmptyMatrix } from 'react-spreadsheet'

import { Button, SimpleDatepicker, SimpleSelect } from 'common/components'
import { toDateWithoutTimeZone } from 'common/dateFormatters'

type SpreadsheetTableProps = {
  defaultData?: Matrix<CellBase<any>>
  onChange?: (data: Matrix<CellBase<any>>) => void
  columns?: (
    | {
        label: string
        type: 'text' | 'date'
        disableBefore?: string
        options?: { id: string; name: string }[]
        key: string
      }
    | { label: string; type: 'select'; disableBefore?: string; options: { id: string; name: string }[]; key: string }
    | { label: string; type: 'date'; disableBefore: string; options?: { id: string; name: string }[]; key: string }
  )[]
}

const INITIAL_ROWS = 10

const SpreadsheetTable = ({ columns, defaultData, onChange }: SpreadsheetTableProps) => {
  const [data, setData] = useState<Matrix<CellBase<any>>>(createEmptyMatrix(INITIAL_ROWS, columns?.length || 10))

  const handleOnChange = (nextData: Matrix<CellBase<any>>) => {
    setData(nextData)
    if (onChange) onChange(nextData)
  }

  const handleAddRow = () => {
    setData((prev) => {
      const newData = [...prev]
      newData.push(new Array(columns?.length || 10).fill({ value: '' }))
      return newData
    })
  }

  useEffect(() => {
    if (defaultData && defaultData.length > 0) setData(defaultData)
  }, [defaultData])

  const getColumnNumber = (column: string) => {
    const columnNumber = columns?.findIndex((x) => x.key === column)
    return columnNumber
  }

  return (
    <>
      <div className="overflow-x-auto">
        <div className="mb-8">
          <Button type="default" className="mr-8" icon="fa fa-plus" onClick={handleAddRow}>
            Add Row
          </Button>
        </div>
        <Spreadsheet
          data={data}
          onChange={handleOnChange}
          DataEditor={(props) => {
            if (columns && columns[props.column].type === 'date') {
              const disableBefore = columns[props.column].disableBefore

              return (
                <div className="width-300px">
                  <SimpleDatepicker
                    format="MM/DD/YYYY"
                    onChange={(date, dateString: string) => {
                      setData((prev) => {
                        const newData = [...prev]
                        newData[props.row][props.column] = { value: dateString }
                        if (onChange) onChange(newData)
                        return newData
                      })
                      props.exitEditMode()
                    }}
                    value={props.cell?.value ? toDateWithoutTimeZone(props.cell?.value) : null}
                    disabledDate={(d) => !d || (disableBefore && d.isBefore(disableBefore))}
                  />
                </div>
              )
            }
            if (columns && columns[props.column].type === 'select')
              return (
                <div className="width-300px">
                  <SimpleSelect
                    onChange={(e) => {
                      setData((prev) => {
                        const newData = [...prev]
                        newData[props.row][props.column] = { value: e }
                        if (onChange) onChange(newData)
                        return newData
                      })
                      props.exitEditMode()
                    }}
                    value={props.cell?.value}
                    items={columns[props.column].options || []}
                  />
                </div>
              )
            else return <DataEditor {...props} />
          }}
          columnLabels={columns.map((x) => x.label)}
        />
      </div>
    </>
  )
}

export default SpreadsheetTable
